import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import NavBar from "../NavBar";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { FaImages, FaPhone, FaLinkedin } from "react-icons/fa";
import "../Styles/ViewPage.css";
import placeholderHead from "../../Assets/logo_rm.png";
import API_BASE_URL from "../../config";

// Import all images for local fallback/default gallery
import gaming1 from "../../Assets/gamingimages01.jpg";
import gaming2 from "../../Assets/esports2.jpeg";
import gaming3 from "../../Assets/esports3.jpeg";

import web3 from "../../Assets/web3.webp";
import web2 from "../../Assets/robotics.jpg";
import web1 from "../../Assets/web1.jpeg";

import design1 from "../../Assets/design1.jpeg";
import design2 from "../../Assets/design2.jpeg";
import design3 from "../../Assets/design3.jpeg";

const categoryImages = {
  "E-Sports": [gaming1, gaming2, gaming3],
  "Professional Development (Web Development & IoT)": [web1, web2, web3],
  "Graphic Design": [design1, design2, design3],
};

const BASE_URL = API_BASE_URL;

// Auto-changing carousel for right side
const AutoCarousel = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (images.length === 0) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 4000); // Change every 4 seconds
    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <div className="auto-carousel">
      {images.length > 0 && (
        <img src={images[currentIndex]} alt={`Slide ${currentIndex + 1}`} className="auto-carousel-img" />
      )}
    </div>
  );
};

const ViewPage = () => {
  const { name } = useParams();
  const [showGallery, setShowGallery] = useState(false);
  const [pillarData, setPillarData] = useState(null);
  const [fetchedEvents, setFetchedEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const decodedName = decodeURIComponent(name);

  // Fetch Pillar Data and Events
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch Pillar Details
        const pillarRes = await fetch(`${BASE_URL}/api/subpillars/${name}`);
        const pillarJson = await pillarRes.json();
        if (pillarJson.success) {
          setPillarData(pillarJson.data);
        }

        // Fetch Events
        const eventsRes = await fetch(`${BASE_URL}/api/events`);
        const eventsJson = await eventsRes.json();
        if (eventsJson.success) {
          const relevantEvents = eventsJson.data.filter(e =>
            e.subPillar === decodedName ||
            (decodedName.includes("Professional Development") && e.subPillar === "Professional Development")
          );
          setFetchedEvents(relevantEvents);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [name, decodedName]);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return placeholderHead;
    if (imagePath.startsWith("http")) return imagePath;
    return `${BASE_URL}/${imagePath.replace(/\\/g, "/")}`;
  };

  const heroImages = categoryImages[decodedName] || [];

  const upcomingEvents = [
    {
      title: "Message from Pillar Head",
      date: pillarData?.pillarMessage || "Welcome to our pillar! Stay tuned for updates.",
      icon: "⚡"
    },
    ...fetchedEvents.map(e => ({
      title: e.title,
      date: `${e.date} at ${e.time}`,
      icon: "📅"
    }))
  ];

  if (fetchedEvents.length === 0) {
    upcomingEvents.push({
      title: `${decodedName} Upcoming Events`,
      date: "No upcoming events scheduled.",
      icon: "📅"
    });
  }

  const handleContactClick = () => {
    if (pillarData?.headPhone) {
      window.location.href = `tel:${pillarData.headPhone}`;
    } else {
      alert("Phone number not available for this pillar head.");
    }
  };

  if (loading) return <div className="view-container"><NavBar /><div style={{ textAlign: 'center', padding: '100px', color: 'white' }}>Loading pillar details...</div></div>;

  return (
    <div className="view-container">
      <NavBar />

      <div className="view-layout">
        <div className="left-side">
          <motion.div
            className="events-section"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: {},
              visible: { transition: { staggerChildren: 0.2 } },
            }}
          >
            <h2 className="events-title">Techstrike {decodedName} Sub Pillar</h2>

            <br />
            <div className="events-grid">
              {upcomingEvents.map((event, idx) => (
                <motion.div
                  key={idx}
                  className="event-card"
                  whileHover={{ scale: 1.05, y: -5 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: idx * 0.2 }}
                >
                  <br />
                  <h3 className="event-title">{event.title}</h3>
                  <p className="event-date">{event.date}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Gallery Button */}
          <div className="gallery-button">
            <button onClick={() => setShowGallery(true)}>
              <FaImages /> Open Gallery
            </button>
          </div>
        </div>

        {/* RIGHT SIDE */}
        <motion.div
          className="right-side"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="head-card">
            <img
              src={getImageUrl(pillarData?.headPhoto)}
              alt={pillarData?.headName || "Pillar Head"}
              className="head-photo"
              onError={(e) => { e.target.src = placeholderHead; }}
            />
            <h2>{pillarData?.headName || "TBD"}</h2>
            <p className="role">{pillarData?.headRole || "Pillar Head"}</p>
            <p className="bio">{pillarData?.headBio || "Leading this pillar to excellence."}</p>
            <div className="head-actions">
              <button onClick={handleContactClick}>
                <FaPhone /> Contact
              </button>
              {pillarData?.headLinkedin && (
                <a href={pillarData.headLinkedin} target="_blank" rel="noreferrer">
                  <FaLinkedin /> LinkedIn
                </a>
              )}
            </div>
          </div>

          <AutoCarousel images={heroImages} />
        </motion.div>
      </div>

      {/* Gallery Modal */}
      {showGallery && (
        <div className="gallery-modal">
          <div className="gallery-content">
            <span className="close-btn" onClick={() => setShowGallery(false)}>✖</span>
            <Carousel infiniteLoop showThumbs={true}>
              {heroImages.map((img, index) => (
                <div key={index}>
                  <img src={img} alt={`Gallery ${index + 1}`} />
                </div>
              ))}
            </Carousel>
          </div>
        </div>
      )}


      <div className="back-link-container">
        <Link to="/Sub_Pillars" className="back-link">← Back to Sub-Pillars</Link>
      </div>
    </div>
  );
};

export default ViewPage;
